import request from 'api/requestUtils'
import { isNumber } from 'lib/maths/mathUtils'
import qs from 'qs'

interface ServerAvailability {
  session_id: string
  external_addon_id: string
  start_time_local: string
  end_time_local: string
  seats: number
  seats_available: number
  price_options: Array<App.AddonAvailabilityPriceOption>
}

function mapAddonAvailability(serverAvailability: ServerAvailability): App.AddonAvailability {
  return {
    sessionId: serverAvailability.session_id,
    externalAddonId: serverAvailability.external_addon_id,
    startTimeLocal: serverAvailability.start_time_local,
    endTimeLocal: serverAvailability.end_time_local,
    seats: serverAvailability.seats,
    seatsAvailable: serverAvailability.seats_available,
    priceOptions: serverAvailability.price_options,
  }
}

export function addonMap(addon: any, currencyCode: string): App.PackageAddonItem {
  const price = addon.prices?.find((price: any) => price.currency_code === currencyCode)
  let trackingPrice = 0
  const audPrice = addon.prices?.find((price: any) => price.currency_code === 'AUD')
  if (audPrice) {
    trackingPrice = audPrice.price ? audPrice.price : audPrice.cost_aud
  }
  return {
    idSalesforceExternal: addon.id_salesforce_external,
    opportunitySalesforceId: addon.fk_opportunity_salesforce_id,
    offerSalesforceId: addon.offer_salesforce_id,
    name: addon.name,
    description: addon.description,
    details: addon.details,
    status: addon.status,
    purchaseLimit: isNumber(addon.purchase_limit) ? addon.purchase_limit : undefined,
    parentPackageId: addon.addon_parent_package_salesforce_id,
    price: price?.price ?? 0,
    value: price?.value ?? 0,
    trackingPrice: trackingPrice ?? 0,
    travelFromDate: addon.travel_from_date,
    travelToDate: addon.travel_to_date,
    bookByDate: addon.book_by_date,
    vendorId: addon.vendor_id,
    vendorBookingEmail: addon.vendor_booking_email,
    vendorContactPhone: addon.vendor_contact_phone,
    imageId: addon.image_cloudinary_external,
    shouldDisplayValue: addon.display_value,
    highlightedText: addon.highlighted_text,
    locationText: addon.location_text,
    channelGroupingId: addon.channel_grouping_id,
    channelFareType: addon.channel_fare_type,
    channelProductId: addon.channel_product_id,
    channelProvider: addon.channel_provider,
    categories: addon.categories ? addon.categories.split(';') : [],
    orderIndex: addon.order_index,
    offlineBookingInstructions: addon.channel_offline_booking_instructions,
    hasOfflineBooking: addon.channel_offline_booking,
    maxDaysBeforeCheckin: addon.channel_max_days_to_book_before_checkin,
    canInstantBook: !!addon.channel_instant_booking,
    booked: addon.channel_booking_made,
    complimentary: addon.complimentary,
    fareTypeText: addon.fare_type_display_value || addon.channel_fare_type || undefined,
    costCurrency: addon.cost_price_currency || 'AUD',
    fromExperiencesServices: false,
  }
}

export async function getOfferPackageAddons(
  offerId: string,
  packageId: string,
  regionCode: string,
  currencyCode: string,
) {
  const response = await request.get<App.ApiResponse<Array<any>>>(`/api/public-offers/${offerId}/packages/${packageId}/addons?region=${regionCode}`)
  return response.result.map(addon => addonMap(addon, currencyCode))
}

interface AvailabilityOptions {
  productCode: string
  startTime: string
  endTime: string
  seats: number
}

export function getAddonAvailability({ productCode, startTime, endTime, seats }: AvailabilityOptions) {
  const query = qs.stringify({
    required_seats: seats,
    start_time_local: startTime,
    end_time_local: endTime,
  })
  const uri = `/api/addons/${productCode}/availability?${query}`

  return request.get<App.ApiResponse<Array<unknown>>>(uri).then(response => response.result.map(mapAddonAvailability))
}

export function bookAddons(data: any) {
  const uri = '/api/addons/booking'

  return request.post<App.ApiResponse<any>, unknown>(uri, data, { credentials: 'include' })
    .then(response => {
      if (response.result.status !== 'CONFIRMED') {
        return Promise.reject('Something went wrong')
      }
      return response.result
    })
}

export function updateAddon(data: any) {
  const uri = '/api/addons/booking'

  return request.patch<App.ApiResponse<any>, unknown>(uri, data, { credentials: 'include' })
    .then(response => {
      if (response.result.status !== 'CONFIRMED') {
        return Promise.reject('Something went wrong')
      }
      return response.result
    })
}
